import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

// Color mode configuration
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
  disableTransitionOnChange: false,
};

// Define custom colors for the sports club
const clubColors = {
  green: {
    50: '#e8f5e8',
    100: '#c3e6c3',
    200: '#9dd69d',
    300: '#77c677',
    400: '#51b651',
    500: '#2e7d32',
    600: '#1b5e20',
    700: '#0f4f14',
    800: '#0a3d0f',
    900: '#042a06'
  },
  clubWhite: {
    50: '#ffffff',
    100: '#fafafa',
    200: '#f5f5f5',
    300: '#f0f0f0',
    400: '#e5e5e5',
    500: '#d0d0d0',
    600: '#b0b0b0',
    700: '#909090',
    800: '#707070',
    900: '#505050'
  },
  // Colors for dark mode
  dark: {
    50: '#1a202c',
    100: '#2d3748',
    200: '#4a5568',
    300: '#718096',
    400: '#a0aec0',
    500: '#cbd5e0',
    600: '#e2e8f0',
    700: '#edf2f7',
    800: '#f7fafc',
    900: '#ffffff'
  }
};

const theme = extendTheme({
  config,
  colors: {
    // Extend color palette with custom club colors
    club: clubColors,
    green: clubColors.green,
    dark: clubColors.dark,
    accent: {
      50: '#f0f9f0',
      100: '#d1f2d1',
      200: '#b3ebb3',
      300: '#94e494',
      400: '#76dd76',
      500: '#57d657',
      600: '#46ab46',
      700: '#358035',
      800: '#245524',
      900: '#122a12'
    }
  },
  semanticTokens: {
    colors: {
      // Define semantic colors that change automatically with theme
      'appBg': {
        default: 'gray.50',
        _dark: 'gray.900'
      },
      'cardBg': {
        default: 'white',
        _dark: 'gray.800'
      },
      'sidebarBg': {
        default: 'green.600',
        _dark: 'green.800'
      },
      'textPrimary': {
        default: 'gray.800',
        _dark: 'gray.100'
      },
      'textSecondary': {
        default: 'gray.600',
        _dark: 'gray.300'
      },
      'borderColor': {
        default: 'gray.200',
        _dark: 'gray.600'
      }
    }
  },
  components: {
    // Customize specific components
    Card: {
      baseStyle: {
        container: {
          boxShadow: 'lg',
          borderRadius: 'xl',
          overflow: 'hidden'
        }
      },
      variants: {
        filled: {
          container: {
            bg: 'cardBg',
            border: '1px solid',
            borderColor: 'borderColor',
            _hover: {
              transform: 'translateY(-2px)',
              boxShadow: 'xl'
            },
            transition: 'all 0.2s ease-in-out'
          }
        },
        outline: {
          container: {
            bg: 'cardBg',
            border: '2px solid',
            borderColor: 'borderColor',
            _hover: {
              transform: 'translateY(-1px)',
              boxShadow: 'lg'
            },
            transition: 'all 0.2s ease-in-out'
          }
        }
      }
    },
    Button: {
      baseStyle: {
        borderRadius: 'lg',
        fontWeight: 'semibold'
      },
      variants: {
        solid: {
          bg: 'green.500',
          color: 'white',
          _hover: {
            bg: 'green.600',
            transform: 'translateY(-1px)',
            boxShadow: 'lg'
          },
          _active: {
            bg: 'green.700',
            transform: 'translateY(0px)'
          }
        },
        outline: {
          borderColor: 'green.500',
          color: 'green.500',
          _hover: {
            borderColor: 'green.600',
            color: 'green.600'
          }
        },
        ghost: {
          color: 'green.500',
          _hover: {
            color: 'green.600'
          }
        }
      }
    },
    Drawer: {
      baseStyle: {
        dialog: {
          bg: 'cardBg'
        }
      }
    }
  },
  styles: {
    global: {
      // Global styles with dark mode support
      body: {
        bg: 'appBg',
        color: 'textPrimary',
        transition: 'backgroundColor 0.3s ease, color 0.3s ease'
      }
    }
  },
  fonts: {
    heading: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    body: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    sloganFont: "sloganFont, serif"
  }
});

export default theme;
